import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AppBar, Screen } from '@/components/layout';
import { Button, Card, Chip, EmptyState, ErrorNote, Spinner } from '@/components/ui';
import { api, apiData } from '@/lib/api';
import { formatRelative } from '@/lib/format';
import type { AdminErrorLog } from '@/types';
import { formatCount } from './lib';

const FILTERS = [
  { value: 'OPEN', label: '미해결' },
  { value: 'RESOLVED', label: '해결됨' },
  { value: '', label: '전체' },
] as const;

/**
 * 에러 매니지먼트 (§3.2) — 처리되지 않은 서버 예외를 지문 단위로 묶어 보여 준다.
 * 같은 오류가 재발하면 해결 표시가 자동으로 풀리고 다시 미해결로 올라온다.
 */
export function AdminErrorsPage() {
  const [status, setStatus] = useState<string>('OPEN');
  const queryClient = useQueryClient();

  const { data, isPending, isError, refetch } = useQuery({
    queryKey: ['admin', 'errors', status],
    queryFn: () => apiData<AdminErrorLog[]>(`/admin/errors${status ? `?status=${status}` : ''}`),
    refetchInterval: 30_000,
    placeholderData: keepPreviousData,
  });
  const errors = data ?? [];

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin', 'errors'] });
    void queryClient.invalidateQueries({ queryKey: ['admin', 'overview'] });
  }

  return (
    <Screen>
      <AppBar title="에러 관리" back />

      <div className="flex gap-2 px-4 pt-3">
        {FILTERS.map((filter) => (
          <Chip
            key={filter.value || 'all'}
            tone={status === filter.value ? 'primary' : 'neutral'}
            selected={status === filter.value}
            onClick={() => setStatus(filter.value)}
          >
            {filter.label}
          </Chip>
        ))}
      </div>

      {isPending && <Spinner />}
      {isError && <ErrorNote message="오류 목록을 불러오지 못했어요." onRetry={() => void refetch()} />}
      {!isPending && errors.length === 0 && (
        <EmptyState
          title={status === 'OPEN' ? '미해결 오류가 없어요' : '기록이 없어요'}
          body={status === 'OPEN' ? '서비스가 조용히 잘 돌고 있어요.' : '필터를 바꿔 보세요.'}
        />
      )}

      <ul className="flex flex-col gap-3 p-4">
        {errors.map((error) => (
          <ErrorCard key={error.id} error={error} onChanged={invalidate} />
        ))}
      </ul>
    </Screen>
  );
}

function ErrorCard({ error, onChanged }: { error: AdminErrorLog; onChanged: () => void }) {
  const act = useMutation({
    mutationFn: (input: { method: 'PATCH' | 'DELETE'; body?: unknown }) =>
      api(`/admin/errors/${error.id}`, { method: input.method, body: input.body }),
    onSuccess: onChanged,
  });

  const open = error.status === 'OPEN';

  return (
    <Card as="li" className="flex flex-col gap-2">
      <div className="flex items-start gap-2">
        <Chip tone={open ? 'accent' : 'neutral'}>{open ? '미해결' : '해결됨'}</Chip>
        <p className="min-w-0 flex-1 break-words font-medium leading-snug">{error.message}</p>
      </div>

      <p className="text-sm text-ink-sub">
        {error.method ? `${error.method} ` : ''}
        {error.path ?? '경로 없음'} · {formatCount(error.count)}회 · 마지막{' '}
        {formatRelative(error.lastSeenAt)}
      </p>

      {error.stack && (
        <details className="rounded-sm bg-bg">
          <summary className="cursor-pointer px-3 py-2 text-sm font-medium text-ink-sub">
            스택 트레이스
          </summary>
          <pre className="overflow-x-auto px-3 pb-3 text-[12px] leading-relaxed text-ink-sub">
            {error.stack}
          </pre>
        </details>
      )}

      <div className="flex gap-2">
        {open ? (
          <Button
            size="sm"
            loading={act.isPending}
            onClick={() => act.mutate({ method: 'PATCH', body: { status: 'RESOLVED' } })}
          >
            해결됨으로 표시
          </Button>
        ) : (
          <Button
            size="sm"
            variant="outline"
            loading={act.isPending}
            onClick={() => act.mutate({ method: 'PATCH', body: { status: 'OPEN' } })}
          >
            다시 열기
          </Button>
        )}
        <Button
          size="sm"
          variant="danger"
          loading={act.isPending}
          onClick={() => {
            if (window.confirm('이 오류 기록을 삭제할까요? 같은 오류가 재발하면 새로 쌓입니다.')) {
              act.mutate({ method: 'DELETE' });
            }
          }}
        >
          삭제
        </Button>
      </div>
    </Card>
  );
}
